import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '@/lib/api';
import { useIntervention } from '@/hooks/useIntervention';
import {
  useUpdateStatus,
  useConfirmDelivery,
  useUpdateNumeroServiceKtm,
} from '@/hooks/useInterventionMutations';
import RepriseDialog from '@/components/RepriseDialog';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';
import StatusBadge, { UrgentBadge } from './StatusBadge';
import DurationPopover from './DurationPopover';
import EditInterventionForm from './EditInterventionForm';
import {
  formatHeureMur,
  formatDurationMinutes,
  formatJourMur,
} from '@/lib/dates';
import type { Statut } from '@/lib/types';
import type { InterventionDetail } from '@/hooks/useSocket';

type Props = {
  interventionId: string | null;
  onClose: () => void;
};

const TYPE_CHANGEMENT_LABELS: Record<string, string> = {
  statut: 'Changement de statut',
  duree: 'Modification de durée',
  mecanicien: 'Changement de mécanicien',
  creneau: 'Changement de créneau',
  cascade: 'Décalage cascade',
};

const CLOSED_STATUSES: ReadonlyArray<Statut> = ['LIVRE', 'ANNULE'];

export default function InterventionDrawer({ interventionId, onClose }: Props) {
  const { data: intervention, isLoading } = useIntervention(interventionId);
  const user = useAuthStore((s) => s.user);
  const [showDurationPopover, setShowDurationPopover] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showDeliveryPopover, setShowDeliveryPopover] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!interventionId) {
      setShowDurationPopover(false);
      setShowActionsMenu(false);
      setShowDeliveryPopover(false);
      setEditing(false);
    }
  }, [interventionId]);

  if (!interventionId) return null;

  const isAtelierOrAdmin = user?.role === 'admin' || user?.role === 'atelier';
  const canReprise = isAtelierOrAdmin || user?.role === 'reception';

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} aria-hidden="true" />
      <aside className="fixed top-0 right-0 h-screen w-[480px] bg-surface-white border-l border-border-light z-50 flex flex-col shadow-lg">
        {isLoading || !intervention ? (
          <div className="flex items-center justify-center h-full text-text-muted text-sm">
            Chargement…
          </div>
        ) : (
          <>
            <header className="border-b border-border-light p-5 shrink-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-text-primary truncate">{intervention.nature}</h2>
                  <p className="text-sm text-text-secondary truncate">
                    {intervention.client.prenom} {intervention.client.nom} ·{' '}
                    {intervention.moto.modele}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <ActionsMenu
                    intervention={intervention}
                    open={showActionsMenu}
                    onToggle={() => setShowActionsMenu((v) => !v)}
                    onClose={() => setShowActionsMenu(false)}
                    closeDrawer={onClose}
                    onEdit={() => setEditing(true)}
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-text-muted hover:text-text-primary text-xl leading-none px-2"
                    aria-label="Fermer"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge statut={intervention.statut} />
                {intervention.urgent && <UrgentBadge />}
              </div>
            </header>

            {editing ? (
              <EditInterventionForm
                intervention={intervention}
                onDone={() => { setEditing(false); onClose(); }}
                onCancel={() => setEditing(false)}
              />
            ) : (
            <>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <Section title="Informations">
                <Field label="Mécanicien">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-5 h-5 rounded-sm flex items-center justify-center text-text-on-orange font-bold text-xs"
                      style={{ backgroundColor: intervention.mecanicien.couleur }}
                      aria-hidden="true"
                    >
                      {intervention.mecanicien.prenom.charAt(0).toUpperCase()}
                    </span>
                    {intervention.mecanicien.prenom} {intervention.mecanicien.nom}
                  </div>
                </Field>
                <Field label="Type">{intervention.typeAgenda}</Field>
                <Field label="Créneau">
                  <div className="flex items-center gap-2">
                    <span className="font-mono">
                      {formatHeureMur(intervention.creneauDebut)} →{' '}
                      {formatHeureMur(intervention.creneauFin)} ·{' '}
                      {formatDurationMinutes(intervention.dureeEstimeeMinutes)}
                    </span>
                    {isAtelierOrAdmin && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowDurationPopover((v) => !v)}
                          className="text-ktm-orange hover:text-ktm-orange-hover text-xs px-2"
                          aria-label="Modifier la durée"
                          title="Modifier la durée"
                        >
                          ✏
                        </button>
                        {showDurationPopover && (
                          <div className="absolute right-0 top-full mt-1 z-10">
                            <DurationPopover
                              interventionId={intervention.id}
                              currentDurationMinutes={intervention.dureeEstimeeMinutes}
                              onClose={() => setShowDurationPopover(false)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Field>
                <Field label="Date RDV">{formatJourMur(intervention.dateRdv)}</Field>
                <Field label="Client">
                  <a
                    href={`tel:${intervention.client.telephone}`}
                    className="font-mono hover:underline cursor-pointer"
                  >
                    {intervention.client.telephone}
                  </a>
                </Field>
                <Field label="Châssis (VIN)">
                  <span className="font-mono text-xs select-all">{intervention.moto.vin}</span>
                </Field>
                {intervention.moto.annee && (
                  <Field label="Année">
                    <span className="font-mono">{intervention.moto.annee}</span>
                  </Field>
                )}
                {intervention.moto.kilometrage !== null && (
                  <Field label="Kilométrage">
                    <span className="font-mono">{intervention.moto.kilometrage.toLocaleString('fr-FR')} km</span>
                  </Field>
                )}
                <Field label="N° service KTM Cloud">
                  <NumeroServiceKtmField
                    interventionId={intervention.id}
                    current={intervention.numeroServiceKtm}
                  />
                </Field>
              </Section>

              {intervention.description && (
                <Section title="Description">
                  <p className="text-sm text-text-secondary whitespace-pre-wrap">
                    {intervention.description}
                  </p>
                </Section>
              )}

              <Section title="Livraison">
                <Field label="Estimée">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono">
                      {formatJourMur(intervention.livraisonEstimee)} ·{' '}
                      {formatHeureMur(intervention.livraisonEstimee)}
                    </span>
                    {intervention.statut === 'ATTENTE_PIECES' && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowDeliveryPopover((v) => !v)}
                          className="text-ktm-orange hover:text-ktm-orange-hover text-xs px-2"
                          aria-label="Modifier livraison estimée"
                          title="Modifier livraison estimée"
                        >
                          ✏
                        </button>
                        {showDeliveryPopover && (
                          <div className="absolute right-0 top-full mt-1 z-10">
                            <DeliveryEstimatePopover
                              interventionId={intervention.id}
                              currentLivraisonEstimee={intervention.livraisonEstimee}
                              onClose={() => setShowDeliveryPopover(false)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Field>
                {intervention.livraisonConfirmee && (
                  <Field label="Confirmée">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-status-termine-text font-semibold">
                        {formatJourMur(intervention.livraisonConfirmee)} ·{' '}
                        {formatHeureMur(intervention.livraisonConfirmee)}
                      </span>
                      <DeliveryDeltaTag
                        estimee={intervention.livraisonEstimee}
                        confirmee={intervention.livraisonConfirmee}
                      />
                    </div>
                  </Field>
                )}
              </Section>

              {intervention.flagDecale && (
                <div className="px-4 py-3 rounded-md bg-ktm-orange-light border border-ktm-orange-border text-text-secondary text-sm">
                  ↪ Cette intervention a été décalée automatiquement suite à une cascade.
                </div>
              )}

              {intervention.statusHistory.length > 0 && (
                <Section title={`Historique (${intervention.statusHistory.length})`}>
                  <ul className="space-y-2 text-xs">
                    {intervention.statusHistory.map((h) => (
                      <li key={h.id} className="border-l-2 border-border-light pl-3">
                        <div className="font-medium text-text-primary">
                          {TYPE_CHANGEMENT_LABELS[h.typeChangement] ?? h.typeChangement}
                        </div>
                        <div className="text-text-muted font-mono">
                          {new Date(h.createdAt).toLocaleString('fr-FR')} · {h.user.prenom} {h.user.nom}
                        </div>
                        {h.commentaire && (
                          <div className="text-text-secondary italic mt-0.5">"{h.commentaire}"</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>

            {(isAtelierOrAdmin || (canReprise && intervention.statut === 'ATTENTE_PIECES')) && (
              <ActionFooter intervention={intervention} canReprise={canReprise} isAtelierOrAdmin={isAtelierOrAdmin} />
            )}
            </>
            )}
          </>
        )}
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">{title}</h3>
      <div className="space-y-2 text-sm">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 items-start text-sm">
      <span className="text-text-muted">{label}</span>
      <span className="text-text-primary">{children}</span>
    </div>
  );
}

/** Menu déroulant "..." : Modifier RDV + Annuler RDV. */
function ActionsMenu({
  intervention,
  open,
  onToggle,
  onClose,
  closeDrawer,
  onEdit,
}: {
  intervention: InterventionDetail;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  closeDrawer: () => void;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/interventions/${id}`),
    onSuccess: () => {
      // L'annulation libère le créneau : il doit réapparaître dans les
      // créneaux libres (['slots']) et disparaître de l'agenda du formulaire
      // (['day-interventions']) immédiatement.
      void qc.invalidateQueries({ queryKey: ['interventions'] });
      void qc.invalidateQueries({ queryKey: ['slots'] });
      void qc.invalidateQueries({ queryKey: ['day-interventions'] });
      toast.success('RDV annulé');
      closeDrawer();
    },
    onError: (err) => {
      const msg = err instanceof HttpError ? err.payload.message : 'Erreur lors de l\'annulation';
      toast.error(msg);
    },
  });

  const isClosed = CLOSED_STATUSES.includes(intervention.statut);

  function handleEdit() {
    onClose();
    onEdit();
  }

  function handleCancel() {
    onClose();
    if (window.confirm("Confirmer l'annulation du RDV ?")) {
      deleteMutation.mutate(intervention.id);
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="drawer-actions-toggle"
        onClick={onToggle}
        className="text-text-muted hover:text-text-primary text-base leading-none px-2 py-1"
        aria-label="Plus d'actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        ⋯
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 bg-surface-white border border-border-light rounded-md shadow-lg w-56 py-1"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="action-edit-rdv"
            onClick={handleEdit}
            disabled={isClosed}
            className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Modifier le RDV
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid="action-cancel-rdv"
            onClick={handleCancel}
            disabled={isClosed || deleteMutation.isPending}
            className="w-full text-left px-3 py-2 text-sm text-status-annule-text hover:bg-status-annule-bg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler le RDV
          </button>
        </div>
      )}
    </div>
  );
}

/** Popover pour modifier la livraison estimée (statut ATTENTE_PIECES uniquement). */
function DeliveryEstimatePopover({
  interventionId,
  currentLivraisonEstimee,
  onClose,
}: {
  interventionId: string;
  currentLivraisonEstimee: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState(toDatetimeLocal(currentLivraisonEstimee));
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: (input: { livraisonEstimee: string; reason?: string }) =>
      api.patch<unknown>(`/interventions/${interventionId}/livraison-estimee`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['interventions'] });
      toast.success('Livraison estimée mise à jour');
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof HttpError ? err.payload.message : 'Erreur de mise à jour';
      toast.error(msg);
    },
  });

  function submit() {
    if (!value) return;
    const iso = new Date(value + ':00.000Z').toISOString();
    mutation.mutate({ livraisonEstimee: iso, reason: reason || undefined });
  }

  return (
    <div className="bg-surface-white border border-border-light rounded-lg shadow-lg p-4 w-80">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-text-primary mb-3">
        Modifier livraison estimée
      </h3>
      <div className="space-y-3">
        <div className="space-y-1">
          <label htmlFor="livraison-estimee" className="input-label">
            Nouvelle estimation
          </label>
          <input
            id="livraison-estimee"
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="input"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="livraison-reason" className="input-label">
            Raison (optionnel)
          </label>
          <textarea
            id="livraison-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Ex: pièce en rupture…"
            className="input py-2 h-auto"
          />
        </div>
        {mutation.isError && (
          <div className="px-3 py-2 rounded-sm bg-status-annule-bg text-status-annule-text text-xs">
            Erreur lors de la mise à jour
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isPending || !value}
            className="btn btn-primary btn-sm"
          >
            {mutation.isPending ? '…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Tag En avance / Retard si écart > 5min entre estimée et confirmée. */
function DeliveryDeltaTag({
  estimee,
  confirmee,
}: {
  estimee: string;
  confirmee: string;
}) {
  const diffMs = new Date(confirmee).getTime() - new Date(estimee).getTime();
  const diffMin = Math.round(diffMs / 60_000);
  const abs = Math.abs(diffMin);
  if (abs <= 5) return null;
  if (diffMin < 0) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded-sm bg-status-termine-bg text-status-termine-text font-bold uppercase tracking-wider">
        ⏰ En avance ({formatDurationMinutes(abs)})
      </span>
    );
  }
  return (
    <span className="text-xs px-1.5 py-0.5 rounded-sm bg-status-annule-bg text-status-annule-text font-bold uppercase tracking-wider">
      ⏰ Retard ({formatDurationMinutes(abs)})
    </span>
  );
}

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

type ActionsProps = {
  intervention: InterventionDetail;
  canReprise: boolean;
  isAtelierOrAdmin: boolean;
};

function ActionFooter({ intervention, canReprise, isAtelierOrAdmin }: ActionsProps) {
  const updateStatus = useUpdateStatus();
  const confirmDelivery = useConfirmDelivery();
  const [showReprise, setShowReprise] = useState(false);

  const transitions: Record<Statut, { primary?: { label: string; to: Statut }; secondary?: { label: string; to: Statut } }> = {
    CONFIRME: { primary: { label: 'Marquer arrivée moto', to: 'EN_ATTENTE' } },
    EN_ATTENTE: { primary: { label: "Démarrer l'intervention", to: 'EN_COURS' } },
    EN_COURS: {
      primary: { label: "Terminer l'intervention", to: 'TERMINE' },
      secondary: { label: 'Attente pièces', to: 'ATTENTE_PIECES' },
    },
    ATTENTE_PIECES: {},
    TERMINE: {},
    LIVRE: {},
    ANNULE: {},
  };

  const actions = transitions[intervention.statut];

  async function doTransition(to: Statut) {
    try {
      await updateStatus.mutateAsync({ id: intervention.id, statut: to });
    } catch {
      // error displayed via badge state
    }
  }

  async function doConfirmDelivery() {
    try {
      await confirmDelivery.mutateAsync({
        id: intervention.id,
        livraisonConfirmee: new Date(Date.now() + 60_000).toISOString(),
      });
    } catch {
      // error
    }
  }

  return (
    <>
      <footer className="border-t border-border-light p-4 shrink-0 flex gap-2">
        {isAtelierOrAdmin && actions.secondary && (
          <button
            type="button"
            onClick={() => doTransition(actions.secondary!.to)}
            disabled={updateStatus.isPending}
            className="btn btn-secondary flex-1"
          >
            {actions.secondary.label}
          </button>
        )}
        {isAtelierOrAdmin && actions.primary && (
          <button
            type="button"
            onClick={() => doTransition(actions.primary!.to)}
            disabled={updateStatus.isPending}
            className="btn btn-primary flex-1"
          >
            {actions.primary.label}
          </button>
        )}
        {intervention.statut === 'ATTENTE_PIECES' && canReprise && (
          <button
            type="button"
            onClick={() => setShowReprise(true)}
            disabled={updateStatus.isPending}
            className="btn btn-primary flex-1"
          >
            Reprendre l'intervention
          </button>
        )}
        {isAtelierOrAdmin && intervention.statut === 'TERMINE' && !intervention.livraisonConfirmee && (
          <button
            type="button"
            onClick={doConfirmDelivery}
            disabled={confirmDelivery.isPending}
            className="btn btn-primary flex-1"
          >
            Confirmer livraison
          </button>
        )}
        {isAtelierOrAdmin && intervention.statut === 'TERMINE' && intervention.livraisonConfirmee && (
          <button
            type="button"
            onClick={() => doTransition('LIVRE')}
            disabled={updateStatus.isPending}
            className="btn btn-primary flex-1"
          >
            Marquer livrée
          </button>
        )}
      </footer>
      {showReprise && (
        <RepriseDialog intervention={intervention} onClose={() => setShowReprise(false)} />
      )}
    </>
  );
}

function NumeroServiceKtmField({
  interventionId,
  current,
}: {
  interventionId: string;
  current: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const update = useUpdateNumeroServiceKtm();

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={30}
          className="input font-mono text-sm flex-1"
          placeholder="SVC-2026-…"
          autoFocus
        />
        <button
          type="button"
          onClick={() => {
            update.mutate(
              { id: interventionId, numeroServiceKtm: draft.trim() || null },
              { onSuccess: () => setEditing(false) },
            );
          }}
          disabled={update.isPending}
          className="px-3 h-9 rounded-md bg-ktm-orange text-text-on-orange text-xs font-medium"
        >
          OK
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="px-3 h-9 rounded-md border border-border-light text-xs"
        >
          Annuler
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(current ?? '');
        setEditing(true);
      }}
      className="text-sm font-mono text-text-primary hover:text-ktm-orange transition-colors text-left"
    >
      {current ?? (
        <span className="text-text-muted">+ Ajouter un n° KTM</span>
      )}
    </button>
  );
}
